import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, Check, X, Plus } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  category?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string, isCustom?: boolean) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  allowCustom?: boolean;
  customPlaceholder?: string;
  groupByCategory?: boolean;
  label?: string;
  required?: boolean;
  className?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner...',
  disabled = false,
  allowCustom = true,
  customPlaceholder = 'Saisissez votre fonction...',
  groupByCategory = false,
  label,
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Détecter si la valeur actuelle est personnalisée (non dans les options)
  const isValueCustom = value && !options.find(opt => opt.value === value || opt.label === value);
  
  // Affichage de la valeur sélectionnée
  const selectedOption = options.find(opt => opt.value === value || opt.label === value);
  const displayValue = selectedOption?.label || (isValueCustom ? value : '');

  // Fermer le menu si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        if (isCustomMode && !customValue) {
          setIsCustomMode(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCustomMode, customValue]);

  // Filtrer les options selon la recherche
  const filteredOptions = useCallback(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    return options.filter(opt => 
      opt.label.toLowerCase().includes(query) ||
      opt.value.toLowerCase().includes(query) ||
      (opt.description && opt.description.toLowerCase().includes(query))
    );
  }, [options, searchQuery]);

  // Grouper les options par catégorie
  const groupedOptions = useCallback(() => {
    const filtered = filteredOptions();
    if (!groupByCategory) return { 'Toutes': filtered };

    return filtered.reduce((acc, opt) => {
      const category = opt.category || 'Autres';
      if (!acc[category]) acc[category] = [];
      acc[category].push(opt);
      return acc;
    }, {} as Record<string, SelectOption[]>);
  }, [filteredOptions, groupByCategory]);

  const handleSelect = (option: SelectOption) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchQuery('');
    setIsCustomMode(false);
    setCustomValue('');
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim(), true);
      setIsOpen(false);
      setIsCustomMode(false);
      setSearchQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isCustomMode && customValue.trim()) {
      e.preventDefault();
      handleCustomSubmit();
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      setIsCustomMode(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setCustomValue('');
    setIsCustomMode(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery('');
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const groups = groupedOptions();
  const hasResults = Object.values(groups).some(g => g.length > 0);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      {/* Bouton principal */}
      <div
        onClick={toggleDropdown}
        className={`
          relative w-full px-3 py-2 border-2 rounded-md cursor-pointer transition-all
          flex items-center justify-between
          ${disabled 
            ? 'bg-gray-100 border-gray-200 cursor-not-allowed' 
            : isOpen 
              ? 'border-[#197ca8] ring-2 ring-[#197ca8]/20' 
              : 'border-gray-300 hover:border-gray-400'
          }
        `}
      >
        <span className={`text-sm truncate ${displayValue ? 'text-gray-900' : 'text-gray-500'}`}>
          {displayValue || placeholder}
        </span>
        <div className="flex items-center space-x-1">
          {displayValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown 
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>

      {/* Menu déroulant */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Barre de recherche */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={isCustomMode ? customValue : searchQuery}
                onChange={(e) => {
                  if (isCustomMode) {
                    setCustomValue(e.target.value);
                  } else {
                    setSearchQuery(e.target.value);
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder={isCustomMode ? customPlaceholder : 'Rechercher...'}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#197ca8] focus:border-[#197ca8]"
              />
            </div>
          </div>

          {/* Mode personnalisé */}
          {isCustomMode ? (
            <div className="p-3">
              <div className="text-xs text-gray-500 mb-2">
                Saisissez une fonction personnalisée :
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsCustomMode(false);
                    setCustomValue('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  disabled={!customValue.trim()}
                  className={`
                    flex items-center px-3 py-1.5 text-sm rounded-md transition-colors
                    ${customValue.trim()
                      ? 'bg-[#197ca8] text-white hover:bg-[#156a92]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Valider
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Liste des options */}
              <div className="max-h-60 overflow-y-auto">
                {hasResults ? (
                  Object.entries(groups).map(([category, categoryOptions]) => (
                    categoryOptions.length > 0 && (
                      <div key={category}>
                        {groupByCategory && Object.keys(groups).length > 1 && (
                          <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                            {category}
                          </div>
                        )}
                        {categoryOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() => handleSelect(option)}
                            className={`
                              px-3 py-2 cursor-pointer transition-colors flex items-center justify-between
                              ${value === option.value || value === option.label
                                ? 'bg-[#197ca8]/10 text-[#197ca8]'
                                : 'hover:bg-gray-50'
                              }
                            `}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{option.label}</div>
                              {option.description && (
                                <div className="text-xs text-gray-500 truncate">{option.description}</div>
                              )}
                            </div>
                            {(value === option.value || value === option.label) && (
                              <Check className="w-4 h-4 text-[#197ca8] flex-shrink-0 ml-2" />
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    Aucun résultat pour "{searchQuery}"
                  </div>
                )}
              </div>

              {/* Option "Autre" */}
              {allowCustom && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomMode(true);
                      setSearchQuery('');
                      setCustomValue(searchQuery); // Pré-remplir avec la recherche actuelle
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    className="w-full px-3 py-2.5 text-left text-sm text-[#197ca8] hover:bg-blue-50 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Autre (saisir manuellement)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
